import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  indexSymbolsQueryOptions,
  momentumQueryOptions,
  signalSessionsQueryOptions,
  steadyQueryOptions,
} from '@/features/signals/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import type { SignalsPaging, SteadyQuery } from '@/schemas/signals'
import { pagingParams, sessionDateParamSchema } from '@/screens/signals/search'

/** The two lists, one at a time (underline tabs, owner 2026-10-15). Steady opens. */
export const ESSENTIALS_TABS = ['steady', 'momentum'] as const
export type EssentialsTab = (typeof ESSENTIALS_TABS)[number]

const { page: momentumPage, pageSize: momentumPageSize } = pagingParams()

/**
 * - `tab` — which list shows: `steady` (default) or `momentum`. A stale value
 *   lands on Steady.
 * - `date` — Steady's session. Momentum takes no date (D3), so the picker
 *   drives only the Steady half of the page.
 * - `page`/`pageSize` — Steady's server paging, 15 a page (owner 2026-10-12).
 * - `momentumPage`/`momentumPageSize` — Momentum's own, so the card's count
 *   never names rows the page cannot reach.
 */
export const essentialsSearchSchema = z.object({
  tab: z.enum(ESSENTIALS_TABS).default('steady').catch('steady'),
  date: sessionDateParamSchema,
  ...pagingParams(),
  momentumPage,
  momentumPageSize,
})
export type EssentialsSearch = z.infer<typeof essentialsSearchSchema>

export const steadyQueryOf = (search: EssentialsSearch): SteadyQuery => ({
  date: search.date ?? null,
  page: search.page,
  pageSize: search.pageSize,
})

export const momentumQueryOf = (search: EssentialsSearch): SignalsPaging => ({
  page: search.momentumPage,
  pageSize: search.momentumPageSize,
})

/**
 * The `/market/signals/essentials` loader. MAIN: Steady for the entry URL
 * when the Steady tab is open. On the Momentum tab nothing is awaited: its
 * read must tolerate a 500 (B7) without taking the page down, so it is
 * prefetched and the card shows its own pending and failed states.
 * SECONDARY: the other list, the sessions, and the index symbols (which rows
 * link).
 */
export async function loadEssentials({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const search = essentialsSearchSchema.parse(location.search)
  if (!preload) {
    prefetchRouteData(queryClient, signalSessionsQueryOptions())
    prefetchRouteData(queryClient, indexSymbolsQueryOptions())
  }
  if (search.tab === 'momentum') {
    prefetchRouteData(queryClient, momentumQueryOptions(momentumQueryOf(search)))
    return
  }
  if (!preload) prefetchRouteData(queryClient, momentumQueryOptions(momentumQueryOf(search)))
  await ensureRouteData(queryClient, steadyQueryOptions(steadyQueryOf(search)))
}
